import { find } from 'lodash';
import { call, getContext, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { initiatePayment, setPrivateBulletinMinimumPromotionPlanModal, setPrivateBulletinSelectPromotionPlanModal } from 'store/state/app/actions';
import { SET_PRIVATE_BULLETIN_MINIMUM_PLAN_MODAL, SET_PRIVATE_BULLETIN_SELECT_PROMOTION_PLAN_MODAL } from 'store/state/app/private-user-monetization/types';
import { privateUserMonetizationEventsSourceSelector } from 'store/state/app/selectors';
import { INITIATE_PAYMENT } from 'store/state/app/types';
import { Domain } from 'store/state/domainData';
import { getActivePromotionPlansSelector } from 'store/state/domainData/selectors/common';
import { LoadType } from '../apiService/types';
import { TRANSITION_SUCCESS } from 'store/state/router/types';
import { routeSelector } from 'store/state/selectors/router';
import { INITIATE_SOURCE } from 'components/private-bulletin-promotion/types';
import { FailedAttemptsStore } from '../privateUserMonetizationWatcher';
import { makeVariantToEventsString } from './utils';

const FAILED_ATTEMPTS_STORE = new FailedAttemptsStore();

type SetPromotionModalType =
  | ReturnType<typeof setPrivateBulletinSelectPromotionPlanModal>
  | ReturnType<typeof setPrivateBulletinMinimumPromotionPlanModal>;

function* promotionPlanModalWatcher(action: SetPromotionModalType, modalType: string) {
  const { sendEvent } = yield getContext('analytics');
  const source = yield select(privateUserMonetizationEventsSourceSelector);

  yield call(sendEvent, action.payload.isOpen ? 'popup_exposed' : 'popup_close', 'popup', {
    event: {
      type: modalType,
      source,
    },
  });
}

function* initiatePaymentWatcher(action: ReturnType<typeof initiatePayment>) {
  try {
    const { sendEvent } = yield getContext('analytics');
    const activePlans: Domain<LoadType.ActivePromotionPlans> = yield select(getActivePromotionPlansSelector);
    const selectedPlanName = find(activePlans.data.activePromotionPlans, { id: action.payload.productId }).variant;
    const source = yield select(privateUserMonetizationEventsSourceSelector);

    yield call(sendEvent, 'user_promotion_plan_select', 'user', {
      event: {
        deal_type: action.payload.initiateSource,
        source,
        promotion_plan: makeVariantToEventsString(selectedPlanName),
      },
    });
  }
  catch (e) {
    const logger = yield getContext('logger');
    logger.error(e);
  }
}

function* paymentResultWatcher() {
  try {
    const { sendEvent } = yield getContext('analytics');
    const route = yield select(routeSelector);
    const isOpen = route.params.payment_modal === 'true';
    const paymentOutcome = route.params.payment_outcome;
    const paymentId = route.params.payment_id;
    const paymentCode = route.params.payment_response_code;
    const source = route.params.payment_source;
    const variant = route.params.payment_variant;
    const dealType = route.params.payment_deal_type;

    const isSuccess = paymentOutcome === 'success';

    if (!isOpen || !paymentOutcome) return;

    if (!isSuccess) {
      FAILED_ATTEMPTS_STORE.increment();
    }

    yield call(sendEvent, isSuccess ? 'user_promotion_plan_submit_success' : 'user_promotion_plan_submit_failed', 'user', {
      event: {
        deal_type: dealType,
        source,
        promotion_plan: makeVariantToEventsString(variant),
        transaction_id: paymentId,
        transaction_error_code: paymentCode,
        dunning_attempt_number: Math.min(FAILED_ATTEMPTS_STORE.get(), 4),
      },
    });
    if (isSuccess) {
      FAILED_ATTEMPTS_STORE.reset();
    }
  }
  catch (e) {
    const logger = yield getContext('logger');
    logger.error(e);
  }
}

export function* monetizationPopupEventsWorker() {
  yield takeEvery(SET_PRIVATE_BULLETIN_SELECT_PROMOTION_PLAN_MODAL, function* (action: ReturnType<typeof setPrivateBulletinMinimumPromotionPlanModal>) {
    const source = yield select(privateUserMonetizationEventsSourceSelector);
    yield* promotionPlanModalWatcher(action, source === 'bulletin_upload_wizard' ? INITIATE_SOURCE.FIRST_TIME : INITIATE_SOURCE.REGULAR);
  });
  yield takeEvery(SET_PRIVATE_BULLETIN_MINIMUM_PLAN_MODAL, function* (action: ReturnType<typeof setPrivateBulletinMinimumPromotionPlanModal>) {
    yield* promotionPlanModalWatcher(action, INITIATE_SOURCE.TRIAL);
  });
  yield takeLatest(INITIATE_PAYMENT, initiatePaymentWatcher);
  yield takeEvery(TRANSITION_SUCCESS, paymentResultWatcher);
}
